import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Dialog, DialogTitle, DialogContent, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Line, Bar } from 'react-chartjs-2';
import LoadingState from './loadingstate';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);


const FireTrendAnalysis = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'year'));
  const [endDate, setEndDate] = useState(dayjs());
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      try {
        // Format dates for the API query
        const start_date = startDate.format('YYYY-MM-DD');
        const end_date = endDate.format('YYYY-MM-DD');

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/analysis/fire-trends?start_date=${start_date}&end_date=${end_date}`);
        
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Failed to fetch fire trend data');
        }
        
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (startDate && endDate && startDate.isSameOrBefore(endDate)) {
      fetchData();
    } else if (startDate && endDate) {
        setError("Start date must be on or before end date.");
    }
  }, [startDate, endDate]); // Re-fetch when dates change
  
  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Number of Fire Alerts' } },
      x: { title: { display: true, text: 'Month' } }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.parsed.y} alerts`
        }
      }
    }
  };
  
  const yearlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Total Fire Alerts' } },
      x: { title: { display: true, text: 'Year' } }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            let tooltip = ` ${label}: ${value} alerts`;
            
            // Calculate percentage change
            if (context.dataIndex > 0) {
              const prevValue = context.dataset.data[context.dataIndex - 1];
              if (prevValue > 0) {
                const percentChange = ((value - prevValue) / prevValue) * 100;
                tooltip += ` (${percentChange.toFixed(1)}% change)`;
              }
            }
            return tooltip;
          }
        }
      }
    }
  };

  const confidenceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Total Alerts' } },
      x: { title: { display: true, text: 'Confidence Level' } }
    }
  };
  
  const handleExpandChart = (chartType) => setExpandedChart(chartType);
  const handleCloseDialog = () => setExpandedChart(null);
  
  // Define the minimum date
  const minDate = dayjs('2015-01-01');
  // Define the maximum date to today
  const maxDate = dayjs();

  return (
    <Box>
      <Grid container spacing={3} alignItems="center" mb={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6">Fire Occurrence Analysis</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              sx={{ width: '100%' }}
              minDate={minDate}
              maxDate={maxDate}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              sx={{ width: '100%' }}
              minDate={minDate}
              maxDate={maxDate}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      
      <LoadingState loading={loading} error={error}>
        <Box>
          {/* Monthly Fire Alerts - Full width */}
          <Paper 
            sx={{ p: { xs: 2, md: 3 }, cursor: 'pointer', mb: 3 }} 
            onClick={() => handleExpandChart('monthly')}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">Monthly Fire Alerts</Typography>
              <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
            </Box>
            <Box sx={{ height: { xs: 300, md: 400 } }}>
              <Line 
                data={data?.monthlyTrends} 
                options={monthlyChartOptions}
              />
            </Box>
          </Paper>

          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              justifyContent: 'space-between',
              alignItems: 'stretch'
            }}
          >
            <Paper 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }} 
              onClick={() => handleExpandChart('yearly')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Year-over-Year Comparison</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 500 } }}>
                <Bar 
                  data={data?.yearlyComparison}
                  options={yearlyChartOptions}
                />
              </Box>
              <Alert severity="info" sx={{ mt: 1.5, textAlign: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" component="div">
                  Hover over a bar to see the exact count and percentage change from the previous year.
                  <br /><b>Note:</b> The current year's data is partial.
                </Typography>
              </Alert>
            </Paper>

            {/* Confidence Level - Full width on mobile, ~48% on desktop */}
            <Paper 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                cursor: 'pointer', 
                flex: { xs: 'none', md: '1 1 48%' },
                display: 'flex',
                flexDirection: 'column'
              }} 
              onClick={() => handleExpandChart('confidence')}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">Confidence Level Distribution</Typography>
                <Typography variant="caption" color="text.secondary">🔍 Click to enlarge</Typography>
              </Box>
              <Box sx={{ height: { xs: 300, md: 500 } }}>
                <Bar 
                  data={data?.confidenceDistribution}
                  options={confidenceChartOptions}
                />
              </Box>
              <Alert severity="info" sx={{ mt: 1.5 }}>
                <Typography variant="caption" component="div">
                  Shows the quality of the fire detection from NASA FIRMS, indicating the likelihood that a pixel contains a fire.
                  <br />• <b>High:</b> &gt;80% confidence. High probability the pixel is a fire.
                  <br />• <b>Nominal:</b> 30%-80% confidence. Medium probability.
                  <br />• <b>Low:</b> &lt;30% confidence. Low probability, may be a false alarm from other heat sources.
                </Typography>
              </Alert>
            </Paper>
          </Box>
        </Box>
        
        {/* Dialog for expanded charts */}
        <Dialog open={!!expandedChart} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            {expandedChart === 'monthly' && 'Monthly Fire Alerts'}
            {expandedChart === 'yearly' && 'Year-over-Year Fire Alert Comparison'}
            {expandedChart === 'confidence' && 'Fire Alert Confidence Level Distribution'}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box height={{ xs: '60vh', md: 500 }}>
              {expandedChart === 'monthly' && (
                <Line data={data?.monthlyTrends} options={monthlyChartOptions} />
              )}
              {expandedChart === 'yearly' && (
                <Bar data={data?.yearlyComparison} options={yearlyChartOptions} />
              )}
              {expandedChart === 'confidence' && (
                <Bar data={data?.confidenceDistribution} options={confidenceChartOptions} />
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </LoadingState>
    </Box>
  );
};

export default FireTrendAnalysis;